import { Archive } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';
import { formatDate } from '~/utils/date';

interface ArchivedBadgeProps {
  /** The row's `archivedAt` stamp — renders nothing when null / absent. */
  archivedAt: string | null | undefined;
  /** Who archived it. `null` on rows archived before changeset 013 — the
   *  tooltip then shows the date alone, never a placeholder actor. */
  archivedBy?: string | null | undefined;
  className?: string;
  /** E2E `data-testid` for the badge; its tooltip is `${testId}-tooltip`. */
  testId?: string;
}

/**
 * "Archived" pill for detail headers, so a deep link into an archived row says
 * why it is missing from the default list — and, when known, who hid it. The
 * list pages don't need it: every row in the Archived view is archived and none
 * in the Active view are.
 */
export function ArchivedBadge({
  archivedAt,
  archivedBy,
  className,
  testId = 'archived-badge',
}: ArchivedBadgeProps) {
  const { t } = useTranslation('common');
  if (!archivedAt) return null;
  const date = formatDate(archivedAt);
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <Badge
          data-testid={testId}
          variant="outline"
          className={cn('text-muted-foreground h-9 gap-1.5 rounded-lg px-3 font-medium', className)}
        >
          <Archive className="h-3.5 w-3.5" aria-hidden="true" />
          {t('listArchive.badge.label')}
        </Badge>
      </TooltipTrigger>
      <TooltipContent data-testid={`${testId}-tooltip`}>
        {archivedBy
          ? t('listArchive.badge.tooltipBy', { date, by: archivedBy })
          : t('listArchive.badge.tooltip', { date })}
      </TooltipContent>
    </Tooltip>
  );
}
